import { Box, Flex, HStack, NumberInput, NumberInputField, Switch, Text } from '@chakra-ui/react';

import { Banner } from '../_ui/components';
import { useCampaignUI } from '../_ui/ui';

/**
 * Letting a campaign move its own traffic to the message that is working (H5).
 *
 * ★ OFF UNLESS SOMEBODY CHOOSES IT, AND THE SCREEN SAYS WHAT CHANGES. An
 * operator approves a specific split; a campaign that quietly reweights it
 * afterwards has changed what was approved. So this is a decision, taken here,
 * with the two guarantees that make it safe stated next to the switch rather
 * than buried in a service file:
 *
 *   · nobody who has already been messaged is moved to another variant
 *   · the losing message is never cut to zero, so it can still recover
 *
 * ★ AND IT SAYS WHEN IT WILL ACT. "It optimises" invites the question every
 * A/B tool gets asked — why has nothing happened? — which is almost always
 * "there is not enough evidence yet". The sample size is on screen and
 * editable, because a campaign of two hundred and a campaign of fifty thousand
 * do not mean the same thing by "enough".
 */

/** The engine's own floor; shown so the number on screen is not a mystery. */
const DEFAULT_MIN_SAMPLE = 50;

export default function VariantOptimisation({ value = {}, variantCount = 0, onChange }) {
	const ui = useCampaignUI();
	const enabled = Boolean(value?.enabled);

	// With one message there is nothing to choose between, and a switch that
	// cannot do anything is worse than no switch.
	if (variantCount < 2) return null;

	const set = (patch) => onChange({ ...(value ?? {}), ...patch });

	return (
		<Box mt={6} borderTopWidth='1px' borderColor={ui.borderSubtle} pt={4}>
			<Flex justify='space-between' align='flex-start' gap={4} mb={1}>
				<Box>
					<Text fontSize='sm' color={ui.textPrimary} fontWeight='medium'>
						Favour whichever message works better
					</Text>
					<Text fontSize='xs' color={ui.textMuted} maxW='72ch' mt={1}>
						Once enough people have received each version, this campaign sends more of whichever one gets more
						replies. Anyone already messaged keeps the version they were sent, and the quieter message is never
						dropped completely — so a slow start can still recover.
					</Text>
				</Box>
				<Switch
					size='sm'
					
					isChecked={enabled}
					onChange={(e) => set({ enabled: e.target.checked })}
				/>
			</Flex>

			{enabled && (
				<Box mt={3}>
					<HStack spacing={2}>
						<Text fontSize='xs' color={ui.textMuted}>
							Wait until each version has reached
						</Text>
						<NumberInput
							size='sm'
							maxW='90px'
							min={10}
							max={100000}
							value={value?.minSample ?? DEFAULT_MIN_SAMPLE}
							onChange={(v) => set({ minSample: Number(v) || DEFAULT_MIN_SAMPLE })}
						>
							<NumberInputField bg={ui.inputBg} borderColor={ui.border} color={ui.textPrimary} />
						</NumberInput>
						<Text fontSize='xs' color={ui.textMuted}>
							people
						</Text>
					</HStack>

					{/* ★ THE SMALL-SAMPLE WARNING IS THE WHOLE POINT OF THE NUMBER.
					    One reply from two sends is a 50% reply rate, and a tool that
					    acted on it would hand the campaign to whichever version got
					    lucky first — then starve the other of the traffic it needed
					    to prove otherwise. */}
					{(value?.minSample ?? DEFAULT_MIN_SAMPLE) < 30 && (
						<Box mt={3}>
							<Banner
								status='warning'
								title='That is a small sample to decide on'
								description='One reply out of two is a 50% reply rate and means almost nothing. Below about thirty people per version, this is likely to back whichever message happened to start well rather than the one that is actually better.'
							/>
						</Box>
					)}
				</Box>
			)}
		</Box>
	);
}
